import { Box, BreadcrumbsProps, darken, lighten, Typography, Stack } from '@mui/material'
import { FeatureFlagIdNewNav2026 } from 'c1gen/features'
import { C1BreadcrumbLinkProps, C1Breadcrumbs } from 'c1ui'
import { avatarPalette } from 'c1ui/theme/palette'
import { C1EditButton } from 'components/core/button/C1EditButton'
import { C1Icon } from 'components/core/icons/C1Icon'
import { PageHeaderEditTitle, PageHeaderEditTitleProps } from 'components/core/page-header/PageHeaderEditTitle'
import { C1Title } from 'components/no-ui/C1Title'
import { useFeatureChecker } from 'components/no-ui/capability'
import { isStringOrFormattedMessage } from 'components/no-ui/helpers/isStringOrFormattedMessage'
import { sxc } from 'components/no-ui/styles/styleTypes'
import { useSimpleModal } from 'lib/modals'
import { LucideIcon } from 'lucide-react'
import React, { useCallback } from 'react'
import { PageHeaderStyles } from './styles'

type PageHeaderIconWrapperProps = {
    icon: LucideIcon
    seed: string
}

const getSeededColor = (seed: string): string => {
    const colorKeys = Object.keys(avatarPalette) as (keyof typeof avatarPalette)[]
    if (!seed) return avatarPalette.blue

    let hash = 0
    for (let i = 0; i < seed.length; i++) {
        hash += seed.charCodeAt(i)
    }

    const colorIndex = hash % colorKeys.length
    return avatarPalette[colorKeys[colorIndex]]
}

const PageHeaderIconWrapper: React.FC<PageHeaderIconWrapperProps> = ({ icon: Icon, seed }) => {
    const hasNewNav = useFeatureChecker(FeatureFlagIdNewNav2026)
    const baseColor = getSeededColor(seed)

    if (!hasNewNav) return null

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 32,
                height: 32,
                borderRadius: 1,
                backgroundColor: lighten(baseColor, 0.85),
                flexShrink: 0,
                mr: 1,
            }}
        >
            <C1Icon icon={Icon} size="sm" sx={{ color: darken(baseColor, 0.2) }} />
        </Box>
    )
}

export type PageHeaderProps = {
    breadcrumbs?: React.ReactElement<BreadcrumbsProps> | (C1BreadcrumbLinkProps | false)[]
    /** Icon to display before the title (typically matches the nav icon for this page) */
    icon?: LucideIcon
    title?: React.ReactNode
    titleString?: string
    updateTitle?: (string) => Promise<unknown>
    subtitle?: string
    actions?: React.ReactNode
    metadata?: React.ReactNode
    padded?: boolean
    editTitleDialogProps?: Omit<PageHeaderEditTitleProps, 'close' | 'title' | 'updateTitle'>
    titleDetails?: React.ReactNode
}

const isBreadcrumbsElement = (
    breadcrumbs: React.ReactElement<BreadcrumbsProps> | (C1BreadcrumbLinkProps | false)[],
): breadcrumbs is React.ReactElement<BreadcrumbsProps> => {
    return !!(breadcrumbs as React.ReactElement<BreadcrumbsProps>).type
}

export const PageHeader = ({
    breadcrumbs,
    icon: Icon,
    title,
    titleString,
    updateTitle,
    subtitle,
    actions,
    metadata,
    padded = false,
    editTitleDialogProps,
    titleDetails,
}: PageHeaderProps): React.ReactElement => {
    const { open } = useSimpleModal()

    const editClick = useCallback(async (): Promise<unknown> => {
        if (updateTitle) {
            return open(PageHeaderEditTitle, { title: titleString, updateTitle, ...editTitleDialogProps })()
        }
        return Promise.resolve()
    }, [editTitleDialogProps, open, titleString, updateTitle])

    const EditButton = (): (React.ReactElement | null) => {
        return updateTitle ? (<C1EditButton onClick={editClick} />) : null
    }

    return (
        <Box component="header" sx={sxc(PageHeaderStyles.container, [padded, PageHeaderStyles.padded])}>
            {titleString ? <C1Title title={titleString} /> : typeof title === 'string' && <C1Title title={title} />}
            <Box sx={PageHeaderStyles.headerSection}>
                {breadcrumbs && <Box sx={PageHeaderStyles.field}>
                    {isBreadcrumbsElement(breadcrumbs)
                        ? breadcrumbs
                        : <C1Breadcrumbs breadcrumbs={breadcrumbs} />
                    }
                </Box>}
                {title && (
                    <Box sx={PageHeaderStyles.field}>
                        {isStringOrFormattedMessage(title) ? (
                            <Stack alignItems="center" gap={1} direction="row">
                                {Icon && <PageHeaderIconWrapper icon={Icon} seed={titleString || (typeof title === 'string' ? title : '')} />}
                                <Typography variant="h2" component="h1" noWrap>
                                    {title}
                                </Typography>
                                <EditButton />
                                {titleDetails && titleDetails}
                            </Stack>
                        ) : (
                            <Stack alignItems="center" gap={1} direction="row">
                                {Icon && <PageHeaderIconWrapper icon={Icon} seed={titleString || ''} />}
                                <Box sx={PageHeaderStyles.title}>{title}</Box>
                                <EditButton />
                                {titleDetails && titleDetails}
                            </Stack>
                        )}
                    </Box>
                )}
                {metadata && (
                    <Box sx={PageHeaderStyles.field}>
                        <Box sx={PageHeaderStyles.statusAndMetadataContainer}>
                            {metadata}
                        </Box>
                    </Box>
                )}
                {subtitle && <Box sx={PageHeaderStyles.field}>
                    <Typography variant="subtitle1" component="p">
                        {subtitle}
                    </Typography>
                </Box>}
            </Box>
            <Box>{actions}</Box>
        </Box>
    )
}
